<template>
  <div class="vrp-tabs">
    <div class="vrp-tabs-header" data-testid="vrp-tabs-container">
      <slot name="header-prefix" />
      <div ref="vrpTabs" class="vrp-tabs-buttons">
        <md-button
          v-for="tab in visibleTabs"
          :key="tab.key"
          class="vrp-tab-button"
          :aria-label="`${tab.label} tab`"
          data-testid="vrp-tab-button"
          @click="selectTab(tab.key)"
        >
          <md-icon v-if="tab.icon" :md-src="tab.icon" />
          <label>{{ tab.label }}</label>
        </md-button>
        <div
          v-for="(tab, tabIndex) in visibleTabs"
          :key="'i-' + tab.key"
          class="indicator-container"
          :data-testid="`active-tab-${tab.label}-indicator-container`"
        >
          <transition name="fade">
            <div
              v-show="activeTabIndex === (leftMostIndex + tabIndex)"
              class="active-tab-indicator"
              data-testid="active-tab-indicator"
            />
          </transition>
        </div>
      </div>
      <div v-if="areNavButtonsVisible" class="nav-buttons">
        <md-button
          class="md-icon-button md-dense arrow-button"
          :disabled="!canGoPrevious || !areNavButtonsVisible"
          aria-label="Previous"
          data-testid="previous-nav-button"
          @click="goPrevious"
        >
          <md-icon>chevron_left</md-icon>
        </md-button>
        <md-button
          class="md-icon-button md-dense arrow-button"
          :disabled="!canGoNext || !areNavButtonsVisible"
          aria-label="Next"
          data-testid="next-nav-button"
          @click="goNext"
        >
          <md-icon>chevron_right</md-icon>
        </md-button>
      </div>
    </div>
    <div
      v-for="tab in tabs"
      v-show="tab.key === activeTabKey"
      :key="tab.key"
      class="tab-container"
      :data-testid="`${tab.label}-tab-content-container`"
    >
      <slot name="item" v-bind="tab" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'vrp-tabs',
  props: {
    tabs: {
      type: Array,
      default: () => []
    },
    activeTabKey: {
      type: [String, Number],
      default: '0',
    },
    resizeable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      leftMostIndex: 0,
      numTabsVisible: this.tabs.length,
    };
  },
  computed: {
    visibleTabs() {
      return this.tabs.slice(
        this.leftMostIndex,
        this.leftMostIndex + this.numTabsVisible
      );
    },
    areNavButtonsVisible() {
      return this.tabs.length > this.numTabsVisible;
    },
    canGoPrevious() {
      return this.leftMostIndex > 0;
    },
    canGoNext() {
      return (this.tabs.length - this.leftMostIndex) > this.numTabsVisible;
    },
    activeTabIndex() {
      return this.tabs.findIndex(tab => tab.key === this.activeTabKey);
    },
  },
  watch: {
    activeTabKey(value) {
      this.scrollToTab(value);
    },
    tabs() {
      this.$nextTick(() => {
        this.onResize();
      });
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.$nextTick(() => {
      this.onResize();
    });
  },
  beforeUpdate() {
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      if (!this.resizeable) {
        return;
      }
      if (this.$refs.vrpTabs) {
        const availableWidth = this.$refs.vrpTabs.offsetWidth;
        this.numTabsVisible = Math.max(1, Math.floor(availableWidth / 230));
        if (this.numTabsVisible >= this.tabs.length) {
          // Reset scroll
          this.leftMostIndex = 0;
        }
      }
    },
    selectTab(key) {
      const tab = this.tabs.find(tab => tab.key === key);
      if (tab) {
        this.$emit('change-tab', tab.key)
      }
    },
    goPrevious() {
      this.leftMostIndex = Math.max(0, this.leftMostIndex - 1);
    },
    goNext() {
      this.leftMostIndex = Math.min(this.tabs.length - 1, this.leftMostIndex + 1);
    },
    columnVisible(colIndex) {
      return colIndex >= this.leftMostIndex && colIndex < (this.leftMostIndex + this.numTabsVisible);
    },
    scrollToTab(tabKey) {
      this.activeTabKey = tabKey
      const tabIndex = this.tabs.findIndex(tab => tab.key === tabKey);
      if (tabIndex > this.leftMostIndex) {
        // not visible, so scroll
        const biggestLeftMostIndex = this.tabs.length - this.numTabsVisible;
        const index = Math.min(tabIndex, biggestLeftMostIndex);
        this.leftMostIndex = index;
      }
    },
  },
}
</script>

<style scoped>
.vrp-tabs {
  display: flex;
  flex-direction: column;
}

.vrp-tabs-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  overflow-x: hidden;
}

.vrp-tabs-buttons {
  flex: 1;
  display: grid;
  transition: 300ms;
  grid-template-rows: repeat(2, auto);
  grid-template-columns: repeat(10, auto);
  overflow-x: hidden;
  align-items: center;
}

.vrp-tab-button {
  font-size: 16px;
  min-width: 150px;
  max-width: unset;
  margin-left: 0;
  margin-right: 8px;
  margin-bottom: 0;
  margin: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.vrp-tab-button >>> .md-button-content {
  display: flex;
  align-items: center;
}

.vrp-tab-button .md-icon {
  margin-right: 4px;
}

.vrp-tab-button .md-icon >>> svg {
  fill: var(--md-theme-default-text-primary-on-background, rgba(0, 0, 0, 0.87)) !important;
}

.vrp-active-tab-button {
  color: #84B2FF;
  border-bottom: 2px solid #84B2FF;
}

.tab-container {
  flex: 1;
}

.nav-buttons {
  display: flex;
  min-width: 32px;
}

.nav-buttons .md-icon-button {
  margin: 0;
}

.indicator-container {
  grid-row: 2;
}

.active-tab-indicator {
  height: 2px;
  width: 100%;
  background-color: #448aff;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.6s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>